import { create } from 'zustand';
import { Player, Tournament } from '../types/types';

export interface PlayerState {
  information: Tournament;
  setInformation: (info: Tournament) => void;
  activeTab: string;
  colorWarningMessage: () => string | null;
  playerWarningMessage: () => string | null;
  setActiveTab: (activeTab: string) => void;
  players: Player[];
  activePlayers: Player[];
  setPlayers: (players: Player[]) => void;
  addPlayer: (player: Player) => void;
  removePlayer: (id: number) => void;
  setActivePlayers: (players: Player[]) => void;
  addActivePlayer: (player: Player) => void;
  removeActivePlayer: (id: number) => void;
  nextTab: () => void;
  prevTab: () => void;
  isNextEnabled: () => boolean;
  isPrevEnabled: () => boolean;
  selectedPlayers: (Player | null)[];
  setSelectedPlayers: (players: (Player | null)[]) => void;
  selectedErsatzspieler: (Player | null)[];
  setSelectedErsatzspieler: (players: (Player | null)[]) => void;
  sortedPlayers: number[];
  setSortedPlayers: (sortedPlayers: number[]) => void;
  sortedErsatzspielers: number[];
  setSortedErsatzspielers: (sortedErsatzspielers: number[]) => void;
  setPlayerColor: (player: Player, color: string) => void;
  setSubstituteColor: (player: Player, color: string) => void;
  capitan: string;
  setCapitan: (capitan: string) => void;
  melder: string;
  setMelder: (melder: string) => void;
  companions: string[];
  setCompanions: (companions: string[]) => void;
  resetTournament: () => void;
}

const tabs = ['capture', 'set', 'preview', 'report'];

export const useTournamentStore = create<PlayerState>((set, get) => ({
  information: {} as Tournament,
  setInformation: (info: Tournament) => set({ information: info }),
  activeTab: 'capture',
  players: [] as Player[],
  activePlayers: [],
  setPlayers: (players) =>
    set({ players: players.map((player) => ({ ...player, color: '' })) }),
  setActiveTab: (activeTab: string) => set({ activeTab }),
  addPlayer: (player) =>
    set((state) => ({ players: [...state.players, player] })),
  removePlayer: (id) =>
    set((state) => ({
      players: state.players.filter((player) => player.id !== id),
    })),
  setActivePlayers: (players) => set({ activePlayers: players }),
  addActivePlayer: (player) =>
    set((state) => ({
      activePlayers: [...state.activePlayers, player],
    })),
  removeActivePlayer: (id) =>
    set((state) => ({
      activePlayers: state.activePlayers.filter((player) => player.id !== id),
    })),
  nextTab: () => {
    const currentIndex = tabs.indexOf(get().activeTab);
    if (currentIndex < tabs.length - 1) {
      set({ activeTab: tabs[currentIndex + 1] });
    }
  },
  prevTab: () => {
    const currentIndex = tabs.indexOf(get().activeTab);
    if (currentIndex > 0) {
      set({ activeTab: tabs[currentIndex - 1] });
    }
  },
  isNextEnabled: () => {
    const currentIndex = tabs.indexOf(get().activeTab);
    return currentIndex < tabs.length - 1 && get().activePlayers.length > 0;
  },
  isPrevEnabled: () => {
    const currentIndex = tabs.indexOf(get().activeTab);
    return currentIndex > 0;
  },
  colorWarningMessage: () => {
    const { selectedPlayers, selectedErsatzspieler } = get();

    let message = '';
    const playersWithoutColor = selectedPlayers
      .filter((player) => player !== null && !player?.color)
      .map((player) => `${player?.name} ${player?.lastName}`);

    const ersatzspielerWithoutColor = selectedErsatzspieler
      .filter((player) => player !== null && !player?.color)
      .map((player) => `${player?.name} ${player?.lastName}`);

    if (
      playersWithoutColor.length > 0 ||
      ersatzspielerWithoutColor.length > 0
    ) {
      message += ` Bitte geben Sie die Abschlagfarbe für jeden Spieler an. Dies ist erforderlich, um fortzufahren. `;
      if (playersWithoutColor.length > 0) {
        message += `Fehlende Farben für Spieler: ${playersWithoutColor.join(', ')}. `;
      }
      if (ersatzspielerWithoutColor.length > 0) {
        message += `Fehlende Farben für Ersatzspieler: ${ersatzspielerWithoutColor.join(', ')}. `;
      }
    }

    return message;
  },

  playerWarningMessage: () => {
    const {
      selectedPlayers,
      selectedErsatzspieler,
      capitan,
      melder,
      information,
    } = get();
    const { numberOfPlayers, numberOfSubstitute } = information;

    let message = '';

    if (
      selectedPlayers.filter((player) => player !== null).length <
      numberOfPlayers
    ) {
      message += `Sie haben nur ${selectedPlayers.filter((player) => player !== null).length} von ${numberOfPlayers} erwarteten Spielern ausgewählt. 🔄 `;
    }

    if (
      selectedErsatzspieler.filter((player) => player !== null).length <
      numberOfSubstitute
    ) {
      message += `Und Sie haben nur ${selectedErsatzspieler.filter((player) => player !== null).length} von ${numberOfSubstitute} erwarteten Ersatzspielern ausgewählt. 🏌️ `;
    }

    if (!capitan) {
      message += 'Und Sie haben keinen Kapitän angegeben. 👨‍✈️ ';
    }

    if (!melder) {
      message += 'Und Sie haben keinen Melder angegeben. 📋 ';
    }

    if (message === 'Achtung: ') {
      return null;
    }

    if (message.length === 0) {
      message +=
        'Bestätigen Sie, dass alle Informationen korrekt eingegeben wurden? Nach dem Klicken auf "Weiter" wird die Meldung an den veranstaltenden Club und den Verband geschickt.';
    } else {
      message += 'Möchten Sie diese Liste trotzdem einreichen?';
    }

    return message;
  },
  selectedPlayers: [],
  setSelectedPlayers: (players) => set({ selectedPlayers: players }),
  selectedErsatzspieler: [],
  setSelectedErsatzspieler: (players) =>
    set({ selectedErsatzspieler: players }),
  sortedPlayers: Array.from({ length: 10 }, (_, i) => i),
  setSortedPlayers: (sortedPlayers) => set({ sortedPlayers }),
  sortedErsatzspielers: Array.from({ length: 4 }, (_, i) => i),
  setSortedErsatzspielers: (sortedErsatzspielers) =>
    set({ sortedErsatzspielers }),
  companions: [],
  setCompanions: (companions) => set({ companions }),
  setPlayerColor: (player, color) => {
    set((state) => {
      const updatedPlayers = state.selectedPlayers.map((p) => {
        if (p?.id === player.id) {
          return { ...p, color };
        }
        return p;
      });
      return { selectedPlayers: updatedPlayers };
    });
  },
  setSubstituteColor: (player, color) => {
    set((state) => {
      const updatedPlayers = state.selectedErsatzspieler.map((p) => {
        if (p?.id === player.id) {
          return { ...p, color };
        }
        return p;
      });
      return { selectedErsatzspieler: updatedPlayers };
    });
  },
  capitan: '',
  setCapitan: (capitan) => set({ capitan }),
  melder: '',
  setMelder: (melder) => set({ melder }),
  resetTournament: () => {
    set({
      activePlayers: [],
      players: [],
      selectedPlayers: [],
      selectedErsatzspieler: [],
      information: {} as Tournament,
      activeTab: 'capture',
    });
  },
}));
